import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  ErrorHandler,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf
} from '@angular/core';
import {
  cobaltIconHelpCircleOutline,
  cobaltIconLogOutOutline,
  CobaltIconRegistry,
  cobaltIconUserAvatarOutline
} from '@cae/cobalt-angular/components/icon';

import { CorePilotRoutingModule } from './core-pilot-routing.module';
import { AuthInterceptor } from './interceptors/auth/auth.interceptor';
import { AppSettings } from './models/settings/app-settings.model';
import { AppSettingsService } from './services/app-settings/app-settings.service';
import { AzureAppInsightsService } from './services/azure-app-insights/azure-app-insights.service';
import { GlobalErrorHandler } from './services/error-handler/error.handler';
import { HeapService } from './services/heap/heap.service';
import { MonitorService } from './services/monitor/monitor.service';
import { WindowRef } from './services/window-ref/window-ref.service';
import { StoreModule } from '../store/store.module';

export function exceptionIfAlreadyLoaded(
  parentModule: any,
  moduleName: string
): void {
  if (parentModule) {
    const msg = `${moduleName} has already been loaded. Import in the AppModule only.`;
    throw new Error(msg);
  }
}
@NgModule({
  imports: [
    CorePilotRoutingModule.standaloneComponents,
    StoreModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    AzureAppInsightsService,
    HeapService,
    WindowRef
  ],
  exports: [CorePilotRoutingModule.standaloneComponents]
})
export class CorePilotModule {
  constructor(
    @Optional() @SkipSelf() parentModule: CorePilotModule,
    private _appSettingsService: AppSettingsService,
    private _azureAppInsightsService: AzureAppInsightsService,
    private _cobaltIconRegistry: CobaltIconRegistry,
    private _heapService: HeapService,
    private _monitorService: MonitorService,
  ) {
    this._cobaltIconRegistry.registerIcons([
      cobaltIconHelpCircleOutline,
      cobaltIconLogOutOutline,
      cobaltIconUserAvatarOutline
    ]);

    exceptionIfAlreadyLoaded(parentModule, 'CorePilotModule');
    this.initHeap();
    this.initAzureAppInsights();
    this.initMonitorService();
  }

  private initHeap(): void {
    this._heapService.initializeHeap().subscribe();
  }

  private initAzureAppInsights(): void {
    this._appSettingsService.appSettings$.subscribe(
      (appSettings: AppSettings) => {
        if (
          !!appSettings.appInsights &&
          !!appSettings.appInsights.instrumentationKey
        ) {
          this._azureAppInsightsService.loadAppInsights(appSettings.appInsights);
        }
      }
    );
  }

  private initMonitorService(): void {
    this._monitorService.setDefaultProperties().subscribe();
  }

  static forRoot(): ModuleWithProviders<CorePilotModule> {
    return {
      ngModule: CorePilotModule
    };
  }
}
